import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useMemo } from 'react';

import { TreadAgentConversationListing } from '~hooks/useTreadAgent/models';
import TreadAgentLoader from '~tread-agent/TreadAgentLoader';
import {
  filterConversationsByTitle,
  formatRelativeShort,
  sortConversationsByMostRecentActivity,
} from '~tread-agent/treadAgentUtils';

interface TreadAgentConversationListProps {
  activeConversationId: string | null;
  conversations: TreadAgentConversationListing[];
  // True only while the very first fetch is in flight, so we show a loader instead of the
  // empty copy (a background refetch keeps the current rows visible).
  isFetching: boolean;
  onSelect: (conversationId: string) => void;
  // The current search text; drives both the filtered rows and which empty copy to show.
  search: string;
}

// The searchable, date-grouped list of recent conversations rendered in the expanded view's
// left pane. Self-contained: it filters and groups its own rows so it stays testable in
// isolation and can be reused wherever a full conversation history is shown.
export default function TreadAgentConversationList({
  activeConversationId,
  conversations,
  isFetching,
  onSelect,
  search,
}: TreadAgentConversationListProps) {
  const theme = useTheme();

  const filteredConversations = useMemo(
    () =>
      filterConversationsByTitle(
        sortConversationsByMostRecentActivity(conversations),
        search,
      ),
    [conversations, search],
  );

  const hasNoResults = filteredConversations.length === 0;

  if (isFetching && conversations.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <TreadAgentLoader size={24} />
      </Box>
    );
  }

  if (hasNoResults) {
    return (
      <Typography
        sx={{
          color: theme.brandV2.colors.treadGray3,
          fontSize: '13px',
          px: 1.5,
          py: 1.5,
        }}
      >
        {search.trim() ? t('tread_agent.no_matching_chats') : t('tread_agent.no_chats')}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.5 }}>
      {filteredConversations.map((conversation) => (
        <Box
          key={conversation.id}
          onClick={() => onSelect(conversation.id)}
          sx={{
            alignItems: 'center',
            backgroundColor:
              conversation.id === activeConversationId
                ? theme.brandV2.colors.treadGray8
                : 'transparent',
            borderRadius: theme.brandV2.borderRadius,
            cursor: 'pointer',
            display: 'flex',
            gap: 1,
            justifyContent: 'space-between',
            mx: 1.5,
            px: 1,
            py: 0.75,
            '&:hover': { backgroundColor: theme.brandV2.colors.treadGray8 },
            '&:hover .conversation-timestamp': { visibility: 'visible' },
          }}
        >
          <Typography noWrap sx={{ fontSize: '12px', fontWeight: 500 }}>
            {conversation.title || t('tread_agent.untitled_chat')}
          </Typography>

          <Typography
            className="conversation-timestamp"
            sx={{
              color: theme.brandV2.colors.treadGray3,
              flexShrink: 0,
              fontSize: '12px',
              visibility: 'hidden',
            }}
          >
            {formatRelativeShort(conversation.lastActivityAt ?? conversation.createdAt)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
